<template>
  <BaseScreen v-bind="screen">
    <BaseAlert
      v-if="isThanksVisible"
      v-bind="thanks"
      slot="middle"
    />
    <BaseAlert
      v-if="isErrorVisible"
      v-bind="error"
      slot="middle"
    />
    <Instruction
      v-bind="instruction"
      class="screen__stretch screen__offset-right"
    />
    <BaseModal
      v-if="isCancelModalVisible"
      v-bind="cancelModal"
      class="screen__overlay"
      slot="root"
    >
      <BaseText v-bind="cancelModal.text" />
    </BaseModal>
  </BaseScreen>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import CONSTANTS from '@/helpers/constants';

import screenMixin from '@/mixins/screen';
import terminalMixin from '@/mixins/terminal';

import Instruction from '@/components/Instruction.vue';
import BaseModal from '@/components/BaseModal.vue';
import BaseText from '@/components/BaseText.vue';

export default {
  name: 'PayScreen',
  mixins: [screenMixin, terminalMixin],
  components: {
    Instruction,
    BaseModal,
    BaseText,
  },
  data() {
    return {
      isPaperOut: false,
      isTerminalReady: false,
      isSaleRegistrationComplete: false,
      isCancelModalVisible: false,
      isPayCanceled: false,
      paymentId: null,
      transactionCode: null,
    };
  },
  staticData: () => ({
    confirmTimeout: null,
  }),
  computed: {
    ...mapState('payment', [
      'account',
      'charges',
      'email',
    ]),
    ...mapGetters('payment', [
      'sum',
      'hasNonpayment',
    ]),
    ...mapGetters('personal', [
      'isAuthenticated',
    ]),
    pendingText() {
      return this.isTerminalReady && this.paymentId
        ? this.$t('pending.pay')
        : this.$t('pending.hardware');
    },
    screenCase() {
      if (this.isThanksVisible) {
        return CONSTANTS.STEP_PAY_SUCCESS;
      }
      if (this.isErrorVisible) {
        if (this.isPaperOut) {
          return CONSTANTS.STEP_NO_PAPER_ERROR;
        }
        if (!this.isTerminalReady) {
          return CONSTANTS.STEP_HARDWARE_ERROR;
        }
        return CONSTANTS.STEP_PAY_ERROR;
      }
      if (this.isTerminalReady && !this.isPending) {
        return CONSTANTS.STEP_PAY;
      }
      return null;
    },
    instruction() {
      return {
        description: {
          text: this.$t('texts.payInstruction'),
          size: 'l',
        },
        imageName: 'pay.jpg',
      };
    },
    cancelModal() {
      return {
        hasCloseButton: false,
        isMiddle: true,
        text: {
          text: this.$t('texts.touchTerminalCancel'),
          size: 'l',
          isCenter: true,
        },
        actions: [
          {
            text: this.$t('actions.understand'),
            action: this.hideCancelModal,
            view: 'outlined',
          },
        ],
      };
    },
    retryAction() {
      return this.isSaleRegistrationComplete
        ? this.onSaleRegistrationComplete
        : this.startTerminalSaleProcess;
    },
    thanks() {
      return {
        icon: {
          name: 'success',
          theme: 'primary',
          size: 'l',
        },
        title: {
          text: this.$t('titles.orderPaid', { account: this.account?.code }),
          size: 'l',
        },
        description: {
          text: this.hasNonpayment
            ? this.$t('texts.takeYourChequeAndMore')
            : this.$t('texts.takeYourCheque'),
          size: 'l',
        },
        actions: [
          {
            text: this.$t('actions.home'),
            action: this.goToHomeScreen,
            view: 'outlined',
          },
          this.isAuthenticated ? {
            text: this.hasNonpayment
              ? this.$t('actions.payAnother')
              : this.$t('actions.backToPersonal'),
            action: this.hasNonpayment
              ? this.goToBalanceScreen
              : this.goToPersonalScreen,
          } : null,
        ].filter(Boolean),
      };
    },
    error() {
      if (this.isPaperOut) {
        return this.noPaperError;
      }
      if (!this.isTerminalReady) {
        return this.terminalError;
      }
      return this.payError;
    },
    terminalError() {
      return {
        icon: {
          name: 'important',
          theme: 'secondary',
          size: 'l',
        },
        title: {
          text: this.$t('titles.badHardware'),
          size: 'l',
        },
        description: {
          text: this.$t('texts.operationNotPossible'),
          size: 'l',
        },
        actions: [
          {
            text: this.$t('actions.home'),
            action: this.goToHomeScreen,
            view: 'outlined',
          },
        ],
      };
    },
    payError() {
      return {
        icon: {
          name: 'important',
          theme: 'secondary',
          size: 'l',
        },
        title: {
          text: this.$t('titles.badConnection'),
          size: 'l',
        },
        description: {
          text: this.$t('texts.payFailed'),
          size: 'l',
        },
        actions: [
          {
            text: this.$t('actions.home'),
            action: this.goToHomeScreen,
            view: 'outlined',
          },
          {
            text: this.$t('actions.retry'),
            action: this.retryErrorAction,
          },
        ],
      };
    },
    noPaperError() {
      return {
        icon: {
          name: 'important',
          theme: 'secondary',
          size: 'l',
        },
        title: {
          text: this.$t('titles.paperOut'),
          size: 'l',
        },
        // description: {
        //   text: this.$t('texts.chequeWasSendedToEmail'),
        //   size: 'l',
        // },
        actions: [
          {
            text: this.$t('actions.home'),
            action: this.goToHomeScreen,
            view: 'outlined',
          },
        ],
      };
    },
    paymentCustomerInfo() {
      return this.email;
    },
    paymentSum() {
      return this.sum;
    },
    paymentItems() {
      return this.charges.map((el) => ({
        name: el.name,
        price: Number(el.value),
        quantity: 1,
        sum: Number(el.value),
        discount: 0,
        tax_type: 7,
      }));
    },
    canLeavePay() {
      return this.isPayCanceled || this.isSaleRegistrationComplete || this.isErrorVisible || this.isThanksVisible;
    },
  },
  created() {
    this.initActions([
      [CONSTANTS.ACTION_BACK, this.goToPersonalScreen],
      [CONSTANTS.ACTION_PAY, this.goToBalanceScreen],
    ]);
    this.startTerminalSaleProcess();
  },
  beforeDestroy() {
    this.clearConfirmTimeout();
  },
  beforeRouteLeave(to, from, next) {
    if (this.canLeavePay) {
      this.clearAll();
      next();
    } else {
      this.showCancelModal();
    }
  },
  methods: {
    ...mapActions('personal', [
      'fetchProfileAccounts',
    ]),
    ...mapActions('payment', [
      'fetchTransactionCode',
      'confirmPayment',
      'createPayment',
      'clearAll',
    ]),
    beforeTerminalCheck() {
      // Этот колбек будет вызван из миксина, перед проверкой терминала
      this.showPending();
    },
    onTerminalError() {
      // Этот колбек будет вызван из миксина, если прилетит ошибка
      this.showError();
    },
    onTerminalPaperOut() {
      // Этот колбек будет вызван из миксина, если кончилась бумага
      this.isPaperOut = true;
      this.showError();
    },
    onTerminalCancel() {
      // Этот колбек будет вызван из миксина при отмене оплаты
      this.isPayCanceled = true;
      this.goBack();
    },
    onTerminalCheckComplete() {
      // Этот колбек будет вызван из миксина, после проверки терминала
      this.isTerminalReady = true;
      this.createPay();
    },
    onSaleRegistrationComplete() {
      // Этот колбек будет вызван из миксина, после оплаты
      this.isSaleRegistrationComplete = true;
      this.setConfirmTimeout();
    },
    async createPay() {
      // Создаем оплату
      try {
        this.paymentId = await this.createPayment(this.isAuthenticated);
        this.hidePending();
      } catch {
        this.showError();
      }
    },
    async confirmPay() {
      // Подтверждаем оплату
      try {
        this.showPending();
        if (!this.transactionCode) {
          this.transactionCode = await this.fetchTransactionCode();
        }
        if (!this.transactionCode) {
          this.setConfirmTimeout();
          return;
        }
        await this.confirmPayment({
          paymentId: this.paymentId,
          transactionCode: this.transactionCode,
        });
        if (this.isAuthenticated) {
          await this.fetchProfileAccounts();
        }
        this.showThanks();
      } catch {
        this.showError();
      }
    },
    setConfirmTimeout() {
      this.confirmTimeout = window.setTimeout(this.confirmPay, 3000);
    },
    clearConfirmTimeout() {
      window.clearTimeout(this.confirmTimeout);
    },
    showCancelModal() {
      this.isCancelModalVisible = true;
    },
    hideCancelModal() {
      this.isCancelModalVisible = false;
    },
    goToBalanceScreen() {
      this.$router.push(this.$router.options.paths.personalBalance);
    },
    goToPersonalScreen() {
      this.$router.push(this.$router.options.paths.personal);
    },
  },
};
</script>
